/**
 * AI Response Contract v1.0 - TypeScript Implementation
 *
 * This file defines the single source of truth for AI service response structure.
 * All AI endpoints MUST return responses that conform to this contract.
 *
 * Based on: /docs/ai-service/ai-response-contract-spec.md
 */

// Core Contract Interface
export interface AIResponseContract {
  // Required: Operation status
  success: boolean;

  // Required: Main data payload
  data: {
    // Required: Human-readable description
    description: string;

    // Required: Search/discovery tags
    tags: string[];

    // Required: Layer-specific metadata
    layerMetadata: Record<string, any>;
  };

  // Required: Processing information
  processingMethod: string; // e.g., "openai-structured", "fallback"
  confidence: number; // 0.0 to 1.0
  processingTimeMs: number; // Processing duration
  fallbackUsed: boolean; // Whether fallback was triggered

  // Optional: Additional metadata
  approach?: string; // e.g., "hybrid-pattern-extraction"

  // Optional: Error information (when success=false)
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
}

// Layer-Specific Metadata Interfaces

export interface SongsLayerMetadata {
  // Tier 1 (Enumerable) - Exact values required
  songTitle: string;
  artistName: string;
  energy: 'High' | 'Medium' | 'Low' | 'Variable';
  vocalType: 'Male' | 'Female' | 'Mixed' | 'Instrumental';
  tempo: 'Fast' | 'Medium' | 'Slow' | 'Variable';
  danceability: 'High' | 'Medium' | 'Low';
  originalArtistGender: 'Male' | 'Female' | 'Non-binary' | 'Other';
  genderSuitability: 'Male' | 'Female' | 'Unisex';
  ageAppropriateness: 'all_ages' | 'teen' | 'adult' | 'mature';

  // Tier 2 (Flexible) - Any string values
  albumName?: string;
  albumArt?: string;
  genre: string; // Single string, not array
  mood: string; // Single string, not array
  subgenre?: string;
  culturalOrigin?: string;
  language?: string;
  targetAudience?: string;

  // Numeric fields
  bpm?: number;
  releaseYear?: number;
  compatibilityScore?: number;

  // Musical fields
  key?: string;
  timeSignature?: string;
}

export interface StarsLayerMetadata {
  // Tier 1 (Enumerable)
  archetype: 'Emerging' | 'Rising' | 'Established' | 'Legendary';
  energyLevel: 'Low' | 'Medium' | 'High' | 'Variable';
  gender: 'Male' | 'Female' | 'Non-binary' | 'Other';
  ageGroup: 'child' | 'teen' | 'adult' | 'senior';
  hairLength: 'Short' | 'Medium' | 'Long' | 'Very Long';
  hairStyle:
    | 'Straight'
    | 'Wavy'
    | 'Curly'
    | 'Coily'
    | 'Braided'
    | 'Updo'
    | 'Ponytail'
    | 'Bun'
    | 'Loose'
    | 'Other';
  makeupType: 'Natural' | 'Glam' | 'Editorial' | 'Character' | 'None';
  colorPalette: 'Neutral' | 'Warm' | 'Cool' | 'Bold' | 'Pastel' | 'Monochrome';

  // Tier 2 (Flexible)
  starName: string;
  hairColor?: string;
  ethnicity?: string;
  performanceStyle?: string;
  performanceContext?: string;
}

export interface LooksLayerMetadata {
  // Tier 1 (Enumerable)
  occasion:
    | 'Casual'
    | 'Work'
    | 'Party'
    | 'Date'
    | 'Travel'
    | 'Sport'
    | 'Formal'
    | 'Wedding'
    | 'Business';
  styleCategory:
    | 'Casual'
    | 'Formal'
    | 'Business'
    | 'Evening'
    | 'Street'
    | 'Athletic'
    | 'Bohemian'
    | 'Vintage'
    | 'Minimalist'
    | 'Glam';
  season: 'Spring' | 'Summer' | 'Fall' | 'Winter' | 'All Season';
  gender: 'Male' | 'Female' | 'Unisex';
  ageGroup: 'child' | 'teen' | 'adult' | 'all';

  // Tier 2 (Flexible)
  outfitName?: string;
  brandNames: string[]; // Array of brand names
  colors: string[]; // Array of colors
  fabricType?: string;
  pattern?: string;
}

export interface MovesLayerMetadata {
  // Tier 1 (Enumerable)
  energyLevel: 'Low' | 'Medium' | 'High';
  complexity: 'Beginner' | 'Intermediate' | 'Advanced';

  // Tier 2 (Flexible)
  moveName?: string;
  danceStyle?: string;
  moveCount?: number;
  duration?: number;
  bpm?: number;
}

export interface WorldsLayerMetadata {
  // Tier 1 (Enumerable)
  timeOfDay: 'Morning' | 'Afternoon' | 'Evening' | 'Night';

  // Tier 2 (Flexible)
  locationName?: string;
  setting?: string;
  mood?: string;
  lighting?: string;
  weather?: string;
  atmosphere?: string;
}

// Typed Contract Responses for Each Layer
export interface SongsAIResponse extends AIResponseContract {
  data: AIResponseContract['data'] & {
    layerMetadata: SongsLayerMetadata;
  };
}

export interface StarsAIResponse extends AIResponseContract {
  data: AIResponseContract['data'] & {
    layerMetadata: StarsLayerMetadata;
  };
}

export interface LooksAIResponse extends AIResponseContract {
  data: AIResponseContract['data'] & {
    layerMetadata: LooksLayerMetadata;
  };
}

export interface MovesAIResponse extends AIResponseContract {
  data: AIResponseContract['data'] & {
    layerMetadata: MovesLayerMetadata;
  };
}

export interface WorldsAIResponse extends AIResponseContract {
  data: AIResponseContract['data'] & {
    layerMetadata: WorldsLayerMetadata;
  };
}

// Union Type for All Layer Responses
export type LayerAIResponse =
  | SongsAIResponse
  | StarsAIResponse
  | LooksAIResponse
  | MovesAIResponse
  | WorldsAIResponse;

// Error Response Format
export interface AIErrorResponse {
  success: false;
  error: {
    message: string;
    code: string;
    details?: any;
  };
  data: {
    description: '';
    tags: [];
    layerMetadata: {};
  };
  processingMethod: 'none';
  confidence: 0;
  processingTimeMs: 0;
  fallbackUsed: false;
}

// Validation Result Types
export interface ContractValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Enum validation maps
export const TIER_1_ENUMS = {
  songs: {
    energy: ['High', 'Medium', 'Low', 'Variable'],
    vocalType: ['Male', 'Female', 'Mixed', 'Instrumental'],
    tempo: ['Fast', 'Medium', 'Slow', 'Variable'],
    danceability: ['High', 'Medium', 'Low'],
    originalArtistGender: ['Male', 'Female', 'Non-binary', 'Other'],
    genderSuitability: ['Male', 'Female', 'Unisex'],
    ageAppropriateness: ['all_ages', 'teen', 'adult', 'mature'],
  },
  stars: {
    archetype: ['Emerging', 'Rising', 'Established', 'Legendary'],
    energyLevel: ['Low', 'Medium', 'High', 'Variable'],
    gender: ['Male', 'Female', 'Non-binary', 'Other'],
    ageGroup: ['child', 'teen', 'adult', 'senior'],
    hairLength: ['Short', 'Medium', 'Long', 'Very Long'],
    hairStyle: [
      'Straight',
      'Wavy',
      'Curly',
      'Coily',
      'Braided',
      'Updo',
      'Ponytail',
      'Bun',
      'Loose',
      'Other',
    ],
    makeupType: ['Natural', 'Glam', 'Editorial', 'Character', 'None'],
    colorPalette: ['Neutral', 'Warm', 'Cool', 'Bold', 'Pastel', 'Monochrome'],
  },
  looks: {
    occasion: [
      'Casual',
      'Work',
      'Party',
      'Date',
      'Travel',
      'Sport',
      'Formal',
      'Wedding',
      'Business',
    ],
    styleCategory: [
      'Casual',
      'Formal',
      'Business',
      'Evening',
      'Street',
      'Athletic',
      'Bohemian',
      'Vintage',
      'Minimalist',
      'Glam',
    ],
    season: ['Spring', 'Summer', 'Fall', 'Winter', 'All Season'],
    gender: ['Male', 'Female', 'Unisex'],
    ageGroup: ['child', 'teen', 'adult', 'all'],
  },
  moves: {
    energyLevel: ['Low', 'Medium', 'High'],
    complexity: ['Beginner', 'Intermediate', 'Advanced'],
  },
  worlds: {
    timeOfDay: ['Morning', 'Afternoon', 'Evening', 'Night'],
  },
} as const;

// Layer type mapping
export type LayerType = 'songs' | 'stars' | 'looks' | 'moves' | 'worlds';

export const LAYER_TYPE_MAP: Record<string, LayerType> = {
  G: 'songs',
  S: 'stars',
  L: 'looks',
  M: 'moves',
  W: 'worlds',
} as const;
